import { FormattedMessage } from 'react-intl';
import { format } from 'date-fns/format';
import { ErrorMessage } from 'formik';
import type { FieldInputProps } from 'formik';
import InputErrorMessage from '../shared/InputErrorMessage';
import formStyles from '../../../styles/forms.module.scss';
import DuetdsWrapper, { type DuetdsDateAdapter } from './DuetdsWrapper';
import type { DuetChangeEvent } from './duet-date-picker';
import { datepickerLocalization } from './datepickerLocalization';
import type { FormikSetFieldValueTouchedForm } from '../shared/formikTypes';
import { getFieldErrorState } from '../shared/formFieldUi';

function padZero(number: number): string | number {
  if (number < 10) {
    return `0${number}`;
  }
  return number;
}

const dateAdapter: DuetdsDateAdapter = {
  parse(value, createDate) {
    const matches = (value ?? '').match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
    if (matches) {
      return createDate(matches[3], matches[2], matches[1]);
    }
    return undefined;
  },
  format(date) {
    return `${padZero(date.getDate())}.${padZero(
      date.getMonth() + 1
    )}.${date.getFullYear()}`;
  },
};

type MinDateResolver = (
  values: Record<string, unknown>
) => Date | null | undefined;

type MinDateProp = Date | MinDateResolver;

interface AccessibleDatepickerInputProps {
  field: FieldInputProps<string>;
  form: FormikSetFieldValueTouchedForm<string | null> & {
    values: Record<string, unknown>;
  };
  labelMsg?: string;
  required?: boolean;
  minDate?: MinDateProp;
}

function AccessibleDatepickerInput({
  labelMsg,
  field,
  required = false,
  form,
  minDate: minDateProp,
}: AccessibleDatepickerInputProps) {
  const { name } = field;

  const minDate =
    typeof minDateProp === 'function'
      ? minDateProp(form.values)
      : (minDateProp ?? null);

  const handleChange = (event: DuetChangeEvent | null) => {
    if (event) {
      form.setFieldValue(name, event.detail.value).catch(() => undefined);
    } else {
      form.setFieldValue(name, null).catch(() => undefined);
    }
  };

  const handleBlur = () => {
    form.setFieldTouched(name, true).catch(() => undefined);
  };

  const { isError } = getFieldErrorState(form, name);

  return (
    <div>
      <label htmlFor={name}>
        {labelMsg && (
          <span className={isError ? formStyles.errorMessage : ''}>
            <FormattedMessage id={labelMsg} />
            {required && <span>*</span>}
          </span>
        )}
      </label>
      <div
        className={`jarvisDuetdsOverride ${
          isError ? formStyles.errorBorderDatepicker : ''
        }`}
      >
        <DuetdsWrapper
          id={name}
          value={field.value}
          onChange={handleChange}
          onBlur={handleBlur}
          dateAdapter={dateAdapter}
          localization={datepickerLocalization.de}
          min={minDate ? format(minDate, 'yyyy-MM-dd') : undefined}
        />
      </div>
      <ErrorMessage name={name} component={InputErrorMessage} />
    </div>
  );
}

export default AccessibleDatepickerInput;
